import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import { SxProps, Theme } from '@mui/material/styles';
import { useQueryClient } from '@tanstack/react-query';
import { t } from 'i18next';
import { useState } from 'react';

import { Department } from '~hooks/useDepartment/models';
import { useDepartment } from '~hooks/useDepartment/useDepartment';
import useMissingRateDepartmentNamesQuery, {
  RATE_DEPARTMENT_MATCH_QUERY_KEY,
} from '~queries/useMissingRateDepartmentNamesQuery';
import { useStores } from '~store';

interface SuggestedRateDepartmentsProps {
  companyId?: string;
  isRateByDepartmentNameRuleOn: boolean;
  onCreated: (department: Department) => void;
  sx?: SxProps<Theme>;
}

export default function SuggestedRateDepartments({
  companyId,
  isRateByDepartmentNameRuleOn,
  onCreated,
  sx,
}: SuggestedRateDepartmentsProps) {
  const { userStore } = useStores();
  const queryClient = useQueryClient();
  const { createDepartment } = useDepartment();
  const [creatingName, setCreatingName] = useState<string>();

  const { canCreateDepartment } = userStore.getPermissions();
  const canSuggestDepartments = isRateByDepartmentNameRuleOn && canCreateDepartment;
  const { missingDepartmentNames } = useMissingRateDepartmentNamesQuery(
    companyId,
    canSuggestDepartments,
  );

  if (!companyId || missingDepartmentNames.length === 0) {
    return null;
  }

  const handleCreate = async (name: string) => {
    setCreatingName(name);

    try {
      const department = await createDepartment(companyId, name);
      await queryClient.invalidateQueries({
        queryKey: [RATE_DEPARTMENT_MATCH_QUERY_KEY, companyId],
      });
      onCreated(department);
    } catch (error) {
      void error;
    } finally {
      setCreatingName(undefined);
    }
  };

  return (
    <Box
      data-test-id="suggested-rate-departments"
      sx={[
        { display: 'flex', flexWrap: 'wrap', gap: 1 },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {missingDepartmentNames.map((name) => (
        <Chip
          key={name}
          color="primary"
          disabled={Boolean(creatingName)}
          icon={
            creatingName === name ? (
              <CircularProgress color="inherit" size={14} />
            ) : undefined
          }
          label={t('administration.department.create_suggested', { name })}
          onClick={() => void handleCreate(name)}
          size="small"
          sx={{ cursor: 'pointer' }}
          variant="filled"
        />
      ))}
    </Box>
  );
}
